/**
 * Iconos de librerías ya activas en el proyecto (lucide-react, react-icons)
 * + set curado útil para SSO / lab / marketing.
 *
 * Valor guardado: "lucide:ShieldCheck" | "ri:FaWhatsapp"
 */
import * as LucideIcons from "lucide-react";
import {
  FaWhatsapp,
  FaLinkedinIn,
  FaFacebookF,
  FaInstagram,
  FaYoutube,
  FaTiktok,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaMapMarkerAlt,
  FaUserMd,
  FaFlask,
  FaHeartbeat,
  FaHardHat,
  FaClipboardList,
  FaAward,
  FaBuilding,
  FaFileMedical,
  FaStethoscope,
  FaShieldAlt,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  MdHealthAndSafety,
  MdScience,
  MdBiotech,
  MdLocalHospital,
  MdWork,
  MdGroups,
  MdVerified,
  MdOutlineScience,
} from "react-icons/md";
import { HiOutlineDocumentReport, HiOutlineAcademicCap } from "react-icons/hi";
import { BiTestTube } from "react-icons/bi";

/** Nombres Lucide usados en el sitio + curados para CMS */
const LUCIDE_NAMES = [
  // Usados en el proyecto
  "Home",
  "Users",
  "Briefcase",
  "Microscope",
  "Receipt",
  "Calculator",
  "FlaskConical",
  "AlertCircle",
  "GraduationCap",
  "ShieldCheck",
  "BookOpen",
  "FileText",
  "Globe",
  "CheckCircle",
  "CheckCircle2",
  "ChevronLeft",
  "ChevronRight",
  "MapPin",
  "ListChecks",
  "Menu",
  "X",
  "Lock",
  "Mail",
  "Loader2",
  "Send",
  "ClipboardList",
  "Building2",
  "Trash2",
  "ShoppingCart",
  "ArrowRight",
  // Curados SSO / lab / UI
  "Award",
  "BadgeCheck",
  "Beaker",
  "Brain",
  "Building",
  "Calendar",
  "Camera",
  "ClipboardCheck",
  "Clock",
  "CloudSun",
  "Droplets",
  "Ear",
  "Eye",
  "FileCheck",
  "FileHeart",
  "Flame",
  "Gem",
  "HandHeart",
  "HardHat",
  "Heart",
  "HeartHandshake",
  "HeartPulse",
  "Hospital",
  "Info",
  "Layers",
  "Leaf",
  "Lightbulb",
  "Link2",
  "Phone",
  "Pill",
  "Quote",
  "Radiation",
  "Scan",
  "Search",
  "Settings",
  "Shield",
  "Snowflake",
  "Sparkles",
  "Star",
  "Stethoscope",
  "Sun",
  "Syringe",
  "Target",
  "Thermometer",
  "TrendingUp",
  "User",
  "UserCheck",
  "Users2",
  "Volume2",
  "Wind",
  "Wrench",
];

const RI_ENTRIES = [
  { key: "FaWhatsapp", label: "WhatsApp", Component: FaWhatsapp },
  { key: "FaLinkedinIn", label: "LinkedIn", Component: FaLinkedinIn },
  { key: "FaFacebookF", label: "Facebook", Component: FaFacebookF },
  { key: "FaInstagram", label: "Instagram", Component: FaInstagram },
  { key: "FaYoutube", label: "YouTube", Component: FaYoutube },
  { key: "FaTiktok", label: "TikTok", Component: FaTiktok },
  { key: "FaPhone", label: "Teléfono", Component: FaPhone },
  { key: "FaEnvelope", label: "Correo", Component: FaEnvelope },
  { key: "FaGlobe", label: "Web", Component: FaGlobe },
  { key: "FaMapMarkerAlt", label: "Ubicación", Component: FaMapMarkerAlt },
  { key: "FaUserMd", label: "Médico", Component: FaUserMd },
  { key: "FaFlask", label: "Matraz", Component: FaFlask },
  { key: "FaHeartbeat", label: "Latido", Component: FaHeartbeat },
  { key: "FaHardHat", label: "Casco", Component: FaHardHat },
  { key: "FaClipboardList", label: "Lista", Component: FaClipboardList },
  { key: "FaAward", label: "Premio", Component: FaAward },
  { key: "FaBuilding", label: "Edificio", Component: FaBuilding },
  { key: "FaFileMedical", label: "Expediente", Component: FaFileMedical },
  { key: "FaStethoscope", label: "Estetoscopio", Component: FaStethoscope },
  { key: "FaShieldAlt", label: "Escudo", Component: FaShieldAlt },
  { key: "FaCheckCircle", label: "OK", Component: FaCheckCircle },
  { key: "FaExclamationTriangle", label: "Alerta", Component: FaExclamationTriangle },
  { key: "MdHealthAndSafety", label: "Salud y seguridad", Component: MdHealthAndSafety },
  { key: "MdScience", label: "Ciencia", Component: MdScience },
  { key: "MdBiotech", label: "Biotecnología", Component: MdBiotech },
  { key: "MdLocalHospital", label: "Hospital", Component: MdLocalHospital },
  { key: "MdWork", label: "Trabajo", Component: MdWork },
  { key: "MdGroups", label: "Grupos", Component: MdGroups },
  { key: "MdVerified", label: "Verificado", Component: MdVerified },
  { key: "MdOutlineScience", label: "Ciencia outline", Component: MdOutlineScience },
  { key: "HiOutlineDocumentReport", label: "Informe", Component: HiOutlineDocumentReport },
  { key: "HiOutlineAcademicCap", label: "Académico", Component: HiOutlineAcademicCap },
  { key: "BiTestTube", label: "Tubo de ensayo", Component: BiTestTube },
];

export const LUCIDE_ICON_OPTIONS = LUCIDE_NAMES.filter((name) => LucideIcons[name]).map((name) => ({
  key: `lucide:${name}`,
  name,
  label: name.replace(/([a-z])([A-Z])/g, "$1 $2"),
  library: "lucide",
  Component: LucideIcons[name],
}));

export const REACT_ICON_OPTIONS = RI_ENTRIES.map((e) => ({
  key: `ri:${e.key}`,
  name: e.key,
  label: e.label,
  library: "react-icons",
  Component: e.Component,
}));

export const LIBRARY_ICON_OPTIONS = [...LUCIDE_ICON_OPTIONS, ...REACT_ICON_OPTIONS];

export function parseLibraryIcon(value) {
  if (!value || typeof value !== "string") return null;
  if (value.startsWith("lucide:")) {
    const name = value.slice("lucide:".length);
    const Component = LucideIcons[name];
    return Component ? { library: "lucide", name, Component } : null;
  }
  if (value.startsWith("ri:")) {
    const name = value.slice("ri:".length);
    const entry = RI_ENTRIES.find((e) => e.key === name);
    return entry ? { library: "react-icons", name, Component: entry.Component } : null;
  }
  return null;
}

export function findLibraryIconOption(value) {
  return LIBRARY_ICON_OPTIONS.find((o) => o.key === value) || null;
}
